import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { api } from '../services/api';
import CategoryIcon from '../components/CategoryIcon';
import CatalogBreadcrumbs from '../components/CatalogBreadcrumbs';
import RichContent, { plainText } from '../components/RichContent';

export default function CategoryPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.getCategories()
      .then((res) => mounted && setCats(res?.data || []))
      .catch(() => mounted && setCats([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const level1 = useMemo(() => {
    return (cats || []).find((c) => c.slug === slug) || null;
  }, [cats, slug]);

  const children = useMemo(() => {
    return Array.isArray(level1?.children) ? level1.children : [];
  }, [level1]);

  return (
    <main className="bg-gray-50 min-h-[70vh]" dir="rtl">
      <section className="bg-white border-b border-gray-200">
        <div className="pp-container py-8">
          <CatalogBreadcrumbs items={level1 ? [level1] : []} />
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-gray-900">
            {loading ? 'در حال بارگذاری...' : (level1?.title || 'دسته‌بندی')}
          </h1>
          <RichContent className="mt-2 text-sm text-gray-600 leading-7" html={level1?.description} />
        </div>
      </section>

      <section className="pp-container py-10">
        {!loading && !level1 && (
          <div className="bg-white border border-gray-200 rounded-3xl p-6 text-sm text-gray-600">
            دسته‌بندی موردنظر پیدا نشد.
          </div>
        )}

        {level1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((c) => (
              <Link
                key={c.id}
                to={`/subcategory/${c.slug}`}
                className="group bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg hover:border-[#005bbb] transition"
              >
                <div className="aspect-[16/9] bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
                  {c.hero_image_url ? (
                    <img src={c.hero_image_url} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-sm text-gray-400">تصویر ثبت نشده</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50">
                      <CategoryIcon
                        category={c}
                        fallback={<Package className="h-6 w-6 text-blue-500" />}
                        imageClassName="h-9 w-9 object-contain"
                      />
                    </span>
                    <h3 className="font-extrabold text-gray-900 group-hover:text-[#005bbb] transition">
                      {c.title}
                    </h3>
                  </div>
                  {(c.short_desc || c.description) && (
                    <p className="mt-2 text-sm text-gray-600 leading-7">
                      {plainText(c.short_desc || c.description)}
                    </p>
                  )}
                  <div className="mt-4 text-xs text-[#005bbb] font-bold">مشاهده محصولات</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {level1 && children.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-3xl p-6 text-sm text-gray-600">
            هنوز زیر‌دسته‌ای برای این دسته ثبت نشده است.
          </div>
        )}
      </section>
      <RichContent html={level1?.bottom_description} className="pp-container pb-10 text-sm text-gray-700" />
    </main>
  );
}
